import { createInstance } from 'i18next';
import { initReactI18next } from 'react-i18next';

export type KdsLanguage = 'he' | 'en';

const resources = {
  he: {
    translation: {
      tab: {
        cooking: 'בהכנה',
        ready: 'מוכן',
        done: 'הוגש',
      },
      notifications: {
        newOrderline: 'שורת הזמנה חדשה',
        screenRefreshed: 'המסך רוענן',
      },
      alerts: {
        soundLocked: 'התראת קול חסומה בדפדפן. לחצו פעם אחת כדי להפעיל קול התראות.',
        enableSound: 'הפעל קול',
      },
      common: {
        close: 'סגור',
      },
      topBar: {
        backToPos: 'חזור ל-POS',
        settings: 'הגדרות',
        switchToLightMode: 'עבור למצב בהיר',
        switchToDarkMode: 'עבור למצב כהה',
      },
      tabBar: {
        menu: 'תפריט',
        search: 'חיפוש',
        searchOrders: 'חיפוש הזמנות',
        closeSearch: 'סגור חיפוש',
        searchPlaceholder: 'שולחן / הזמנה / מנה',
      },
      overview: {
        title: 'סקירה כללית',
        uncategorized: 'ללא קטגוריה',
        allCategories: 'הכל',
        close: 'סגור סקירה כללית',
      },
      state: {
        loadingOrders: 'טוען הזמנות...',
        noActiveOrders: 'אין הזמנות פעילות',
      },
      print: {
        noPrinterSelected: 'לא נבחרה מדפסת',
        selectPrinterBeforePrint: 'יש לבחור מדפסת KDS בהגדרות לפני אישור הדפסה',
        queueFailed: 'נכשלה יצירת משימת הדפסה',
        cancel: 'ביטול',
        print: 'הדפס',
        sending: 'שולח...',
        printTicket: 'הדפס פתק',
        cancelPrint: 'ביטול הדפסה',
        confirmPrint: 'אישור הדפסה',
        timeLabel: 'שעה',
        orderNumberLabel: 'מספר הזמנה',
        tableLabel: 'שולחן',
        dinersLabel: 'סועדים',
        waiterLabel: 'מלצר',
        newBadgePlaceholder: 'חדש',
        diningTypePlaceholder: '--',
        diningTypeDineIn: 'לשבת',
        diningTypeTakeAway: 'לקחת',
        placeholderValue: '--',
      },
      settings: {
        title: 'הגדרות',
        close: 'סגור הגדרות',
        language: 'שפה',
        languageOptionHebrew: 'עברית',
        languageOptionEnglish: 'English',
        ticketZoom: 'גודל כרטיס',
        orderSort: 'סדר הזמנות',
        orderTypeFilter: 'סוג הזמנה',
        kitchenPrinter: 'מדפסת מטבח',
        loadingPrinters: 'טוען מדפסות...',
        noPrinter: 'ללא מדפסת',
        notificationSound: 'צליל התראה',
        notificationSoundNone: 'ללא צליל',
        notificationSoundTest: 'צליל בדיקה',
        notificationMutedLine1: 'המערכת משתמשת בקובץ WAV עבור התראות KDS.',
        notificationMutedLine2: 'אם אין אינטראקציה ראשונית בדפדפן, הקול עלול להיות חסום עד לחיצה אחת.',
        mergeOrders: 'איחוד הזמנות',
        showDoneTab: 'הצג "הוגש"',
        orderSortOptions: {
          duration: 'לפי זמן',
          product_count: 'לפי כמות מוצרים',
          table: 'לפי מספר שולחן',
        },
        orderTypeFilterOptions: {
          all: 'הכל',
          dine_in: 'לשבת',
          take_away: 'לקחת',
        },
      },
      card: {
        openReturnMenu: 'פתח תפריט החזרה',
        edit: 'עריכה',
        exitEdit: 'צא מעריכה',
        pinOrder: 'נעץ הזמנה',
        unpinOrder: 'בטל נעיצת הזמנה',
        waiterPlaceholder: '--',
        partialReady: 'מוכן חלקית',
        partialDone: 'הוגש חלקית',
        dineIn: 'מסעדה',
        takeAway: 'לקחת',
        paid: 'שולם',
        toggleStatus: 'החלף סטטוס של {{productName}}',
        markAllReady: 'סמן הכל מוכן',
        markAllCooking: 'סמן הכל בהכנה',
        markAllDone: 'סמן הכל הוגש',
        hideOrder: 'הסתר הזמנה',
        buttonReady: 'מוכן',
        buttonCooking: 'בהכנה',
        buttonDone: 'הוגש',
        buttonHide: 'הסתר',
      },
    },
  },
  en: {
    translation: {
      tab: {
        cooking: 'Cooking',
        ready: 'Ready',
        done: 'Done',
      },
      notifications: {
        newOrderline: 'New order line',
        screenRefreshed: 'Screen refreshed',
      },
      alerts: {
        soundLocked: 'Browser blocked alert sound. Tap once to enable notification audio.',
        enableSound: 'Enable sound',
      },
      common: {
        close: 'Close',
      },
      topBar: {
        backToPos: 'Back to POS',
        settings: 'Settings',
        switchToLightMode: 'Switch to light mode',
        switchToDarkMode: 'Switch to dark mode',
      },
      tabBar: {
        menu: 'Menu',
        search: 'Search',
        searchOrders: 'Search orders',
        closeSearch: 'Close search',
        searchPlaceholder: 'Table / order / line',
      },
      overview: {
        title: 'Overview',
        uncategorized: 'Uncategorized',
        allCategories: 'All',
        close: 'Close overview',
      },
      state: {
        loadingOrders: 'Loading orders...',
        noActiveOrders: 'No active orders',
      },
      print: {
        noPrinterSelected: 'No printer selected',
        selectPrinterBeforePrint: 'Please choose a KDS printer in settings before confirming print.',
        queueFailed: 'Failed creating print job',
        cancel: 'Cancel',
        print: 'Print',
        sending: 'Sending...',
        printTicket: 'Print ticket',
        cancelPrint: 'Cancel print',
        confirmPrint: 'Confirm print',
        timeLabel: 'Time',
        orderNumberLabel: 'Order #',
        tableLabel: 'Table',
        dinersLabel: 'Guests',
        waiterLabel: 'Waiter',
        newBadgePlaceholder: 'NEW',
        diningTypePlaceholder: '--',
        diningTypeDineIn: 'Dine-in',
        diningTypeTakeAway: 'Take-Away',
        placeholderValue: '--',
      },
      settings: {
        title: 'Settings',
        close: 'Close settings',
        language: 'Language',
        languageOptionHebrew: 'עברית',
        languageOptionEnglish: 'English',
        ticketZoom: 'Ticket size',
        orderSort: 'Order sorting',
        orderTypeFilter: 'Order type',
        kitchenPrinter: 'Kitchen printer',
        loadingPrinters: 'Loading printers...',
        noPrinter: 'No printer',
        notificationSound: 'Notification sound',
        notificationSoundNone: 'No sound',
        notificationSoundTest: 'Test sound',
        notificationMutedLine1: 'KDS alerts use a WAV notification sound.',
        notificationMutedLine2: 'Without a first user interaction, some browsers may block audio playback.',
        mergeOrders: 'Merge Orders',
        showDoneTab: 'Show "Done"',
        orderSortOptions: {
          duration: 'By time',
          product_count: 'By product count',
          table: 'By table number',
        },
        orderTypeFilterOptions: {
          all: 'All',
          dine_in: 'Dine-in',
          take_away: 'Take-away',
        },
      },
      card: {
        openReturnMenu: 'Open return menu',
        edit: 'Edit',
        exitEdit: 'Exit edit',
        pinOrder: 'Pin order',
        unpinOrder: 'Unpin order',
        waiterPlaceholder: '--',
        partialReady: 'Partially ready',
        partialDone: 'Partially served',
        dineIn: 'Dine in',
        takeAway: 'Take-Away',
        paid: 'Paid',
        toggleStatus: 'Toggle status of {{productName}}',
        markAllReady: 'Mark all ready',
        markAllCooking: 'Mark all cooking',
        markAllDone: 'Mark all done',
        hideOrder: 'Hide order',
        buttonReady: 'Ready',
        buttonCooking: 'Cooking',
        buttonDone: 'Done',
        buttonHide: 'Hide',
      },
    },
  },
} as const;

export const kdsI18n = createInstance();

void kdsI18n
  .use(initReactI18next)
  .init({
    resources,
    lng: 'he',
    fallbackLng: 'he',
    interpolation: { escapeValue: false },
  });
